// The Agents place's pure helpers: what a budget meter shows, how a policy may be set, how an
// activity row reads, and the receptionist's hours in words. Kept apart from the screens so they can
// be tested.

import { localTimeRangeText, timeParts, timeText } from '@flowology/kernel';
import { money } from '../books/model.ts';

export type Policy = 'auto' | 'ask_first' | 'never';
export const POLICIES: readonly Policy[] = ['auto', 'ask_first', 'never'];
export const POLICY_LABEL: Record<Policy, string> = {
  auto: 'Automatic',
  ask_first: 'Ask first',
  never: 'Never',
};
const RANK: Record<Policy, number> = { never: 0, ask_first: 1, auto: 2 };

/** "Voice receptionist" → "VR". */
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** How full a budget is: the share used (0–1, capped) and whether it is near (80 %) or at its cap. */
export function budgetLevel(spentCents: number, budgetCents: number | null) {
  if (budgetCents === null)
    return { share: 0, level: 'none' as const, text: `${money(spentCents)} · no cap` };
  const share = budgetCents === 0 ? 1 : spentCents / budgetCents;
  const level = share >= 1 ? ('bad' as const) : share >= 0.8 ? ('warn' as const) : ('ok' as const);
  return { share: Math.min(1, share), level, text: `${money(spentCents)} of ${money(budgetCents)}` };
}

/** A policy a person may choose: not above the action's maximum, and never for a blocked action. */
export const canChoose = (p: Policy, max: Policy, blocked: string | null) => !blocked && RANK[p] <= RANK[max];

export interface ToolPolicy {
  action: string;
  title: string;
  kind: string;
  default: Policy;
  max: Policy;
  current: Policy;
  setting: Policy | null;
  blocked: string | null;
  /** An agent's limits on it (M10): null for a query. */
  limit: ToolLimit | null;
}

/** agents.limit.<action>: the ceiling (where it carries an amount) and the count a day, and their defaults. */
export interface ToolLimit {
  hasAmount: boolean;
  maxCents: number | null;
  perDay: number | null;
  defaultMaxCents: number | null;
  defaultPerDay: number | null;
  /** The company set its own; clearing goes back to the defaults. */
  set: boolean;
}

/** "Up to $2,500 · 20 a day", "No limit", for a limit as it applies now. */
export function limitText(l: ToolLimit, money: (cents: number) => string): string {
  const parts = [
    ...(l.hasAmount && l.maxCents !== null ? [`up to ${money(l.maxCents)}`] : []),
    ...(l.perDay !== null ? [`${l.perDay} a day`] : []),
  ];
  return parts.length ? parts.join(' · ').replace(/^./, (c) => c.toUpperCase()) : 'No limit';
}

/** A count a day as typed: blank is none, a whole number 1–10,000 is one; anything else is null. */
export function parseCount(text: string): number | 'none' | null {
  const t = text.trim();
  if (t === '') return 'none';
  if (!/^\d{1,5}$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 10_000 ? n : null;
}

/** The warning share as typed: 1–99. */
export function parsePercent(text: string): number | null {
  const t = text.trim().replace(/%$/, '').trim();
  if (!/^\d{1,2}$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= 99 ? n : null;
}

/** The line under a policy row: why it is limited, or what its default is. */
export function policyCaption(t: ToolPolicy): string {
  if (t.blocked) return t.blocked;
  if (t.max === 'never') return `Agents can never “${t.title}”.`;
  const base = t.setting
    ? `Set by the company (default ${POLICY_LABEL[t.default]}).`
    : `Default: ${POLICY_LABEL[t.default]}.`;
  if (t.max === 'ask_first') return `${base} Most it can be: Ask first.`;
  return t.default === 'auto' ? base : `${base} You may make it automatic.`;
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  dispatcher: 'Dispatcher',
  csr: 'CSR',
  technician: 'Technician',
  bookkeeper: 'Bookkeeper',
  warehouse: 'Warehouse',
};
/** "csr" → "CSR"; a company's own role keeps its key. */
export const roleLabel = (key: string) => ROLE_LABEL[key] ?? key;

/** The live changes that move the Agents page's counts: a request decided or failed, an agent stopped or started. */
export const AGENT_EVENTS = /^(approval|agent)\./;

export type Mode = 'automatic' | 'asked' | 'refused' | 'failed';
export const MODE_CHIP: Record<Mode, [string, string]> = {
  automatic: ['ok', 'automatic'],
  asked: ['warn', 'asked first'],
  refused: ['bad', 'refused'],
  failed: ['bad', 'did not work'],
};

/**
 * An asked step's chip once a person decided it (QA-858): "approved by Demo Owner", "declined by
 * Demo Owner"; the mode's own chip while it waits.
 */
export function stepChip(s: {
  mode: Mode;
  decision?: { status: 'approved' | 'declined' | 'expired'; by: string | null } | null;
}): [string, string] {
  const d = s.decision;
  if (!d) return MODE_CHIP[s.mode];
  if (d.status === 'expired') return ['muted', 'expired, not decided'];
  const by = d.by ? ` by ${d.by}` : '';
  return d.status === 'approved' ? ['ok', `approved${by}`] : ['bad', `declined${by}`];
}

export interface ReceptionistHours {
  mode: 'after_hours' | 'always';
  open: string;
  close: string;
  days: number[];
}
const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAYS = DAY.map((label, i) => ({ n: i + 1, label }));

/** "Answers outside office hours (Mon–Fri 8 a.m.–5 p.m.)…" or "Answers every call…". */
export function hoursText(h: ReceptionistHours): string {
  if (h.mode === 'always') return 'Answers every call, day and night.';
  const days = [...h.days].sort((a, b) => a - b);
  const run = days.length > 1 && days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
  const dayText = !days.length
    ? 'no office days'
    : run
      ? `${DAY[days[0]! - 1]}–${DAY[days[days.length - 1]! - 1]}`
      : days.map((d) => DAY[d - 1]).join(', ');
  return `Answers outside office hours (${dayText} ${localTimeRangeText(h.open, h.close)}); in office hours calls ring the office.`;
}

/** "2:05 a.m." in the company's time zone (the browser's until it's known). */
export const clock = (iso: string, timeZone?: string) =>
  timeText(iso, timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);

/** The same, "2:05" and "a.m." apart. */
export const clockParts = (iso: string, timeZone?: string) =>
  timeParts(iso, timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * An agent's task as the office reads it (QA-349). The task is written for the model, with the
 * record's id beside its number ("Estimate 1 (estimate 01a1…)"); people get the number alone, and
 * never an id.
 */
export function taskText(task: string): string {
  return task
    .replace(new RegExp(String.raw`\s*\([a-z_ ]+ ${UUID.source}\)`, 'gi'), '')
    .replace(new RegExp(String.raw`\s*${UUID.source}`, 'gi'), '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * An action as a person reads it: its title ("Pay vendor bills"), or, when only its name is known,
 * the name in words ("estimates.soldNotBooked" → "Estimates: sold not booked"). The dotted name
 * stays on hover, as approvals have it (F-04).
 */
export function actionWords(action: string | null, title?: string | null): string {
  if (title) return title;
  if (!action) return 'A step';
  const [area = '', verb = ''] = action.split('.');
  const words = (x: string) =>
    x
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim();
  const head = words(area).replace(/^./, (c) => c.toUpperCase());
  return verb ? `${head}: ${words(verb)}` : head;
}

/** A tool's kind in the owner's words: whether it only reads, or changes things. */
export const KIND_WORDS: Record<string, string> = { query: 'reads only', command: 'makes changes' };

/** Dollars typed into a budget field → cents; empty means no cap; null when it isn't money. */
export function parseBudget(text: string): number | null | 'none' {
  const t = text.trim().replace(/[$,\s]/g, '');
  if (t === '') return 'none';
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/** How many of the day's steps the panel reads at a time; the rest come on "Load more" (list caps M16). */
export const ACTIVITY_PAGE = 25;

/** agents.activity's input for an agent and a mode picked (none: every one), a page at a time. */
export const activityInput = (agentKey: string, mode: Mode | ''): Record<string, unknown> => ({
  ...(agentKey ? { agentKey } : {}),
  ...(mode ? { mode } : {}),
  limit: ACTIVITY_PAGE,
});
